import React, {useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Transition} from 'react-transition-group';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import SpinSmall from '../../components/SpinSmall';
import NotFoundPage from '../NotFoundPage';
import {bankingActions} from '../../state/actions/banking.actions';
import {cardActions} from '../../state/actions/card.actions';
import {expenseActions} from '../../state/actions/expense.actions';
import {subscriptionActions} from '../../state/actions/subscription.actions';
import {transactionActions} from '../../state/actions/transaction.actions';
import {
  StyledCardPageContainer,
  StyledCardPageContainerSpace,
  StyledCardPageTransactionContainer
} from './StyledCardPage';
import PageContainer from '../../components/PageContainer';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader/BalancePageHeader';
import AuthenticationWindow from '../../components/pages/CardsPage/AuthenticationWindow';
import CardDetailsSection from '../../components/pages/CardPage/CardDetailsSection';
import TransactionsPanel from '../../components/pages/CardPage/TransactionsPanel';
import TransactionExpenseDetailsPanel from '../../components/pages/TransactionsPage/TransactionExpenseDetailsPanel';
import routes from '../../routes/routes.json';
import {
  cardsHelpers,
  objectHelpers,
  scaHelpers,
  systemHelpers,
  transactionsHelpers
} from '../../utils/helpers';
import {
  SCAActionsConstants,
  transactionsRequestLimit
} from '../../constants';
import {firebaseEvents} from '../../snippets/firebase';
import {useHasAccess, useIsEnabledUserOverview} from '../../hooks';

const duration = 400;

const detailsTransitionStyles = {
  default: {
    opacity: 1,
    transition: `${duration}ms ease-in-out`
  },
  entering: {transform: 'translateX(0)', opacity: 0.75, width: 448},
  entered: {transform: 'translateX(0)', opacity: 1, width: 448},
  exiting: {transform: 'translateX(200%)', opacity: 0.25, width: 0, visibility: 'hidden'},
  exited: {transform: 'translateX(200%)', opacity: 0, width: 0, visibility: 'hidden'}
}

const {
  TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION,
  TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION
} = SCAActionsConstants;

const {logEvent} = systemHelpers;

const {isValidValue} = objectHelpers;

const CardPage = ({
  getBatchExpenses,
  getBankingCardDetails,
  getMainCardDetails,
  getSubscription,
  getTransactionsList,
  getUserDetails,
  employees,
  employeeId,
  isAdmin,
}) => {
  const uriParams = useParams();
  const [t] = useTranslation(['main', 'subscriptions', 'cards']);
  const [cardDetails, setCardDetails] = useState({
    bankingData: null,
    mainData: null
  });
  const [pageLoading, setPageLoading] = useState({loading: true, exists: true});
  const [transactions, setTransactions] = useState({transactions: [], isLoaded: false});
  const [transactionsWithExpense, setTransactionsWithExpense] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [activeAuthAction, setActiveAuthAction] = useState(null);
  const [hasBankingUser, setHasBankingUser] = useState(true);
  const [loadMoreProps, setLoadMoreProps] = useState({cursor: null, loading: false, isEnabled: false});
  const [isOpenDetails, setIsOpenDetails] = useState({card: true, transaction: false});
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [selectedTransactionRowIndex, setSelectedTransactionRowIndex] = useState(undefined);

  const hasEditAccess = useHasAccess('PUT_UPDATE_CARD');

  const cardDetailsNodeRef = useRef(null);
  const transactionDetailsNodeRef = useRef(null);

  const {cardId, userId} = useMemo(() => ({
    cardId: uriParams?.cardId,
    userId: uriParams?.userId
  }), [uriParams]);

  const isEnabledEdit = useMemo(() => hasEditAccess || employeeId === userId, [hasEditAccess, employeeId, userId]);

  const cardNumber = useMemo(() => cardsHelpers.hideCardNumber(cardDetails?.mainData?.masked_pan, 6), [cardDetails]);

  const cardHold = useMemo(() => {
    let hold = false;
    if (pageLoading.exists && !pageLoading.loading) {
      hold = cardDetails?.bankingData?.status_code === undefined;
    }
    if (!hasBankingUser) hold = true;
    return hold;
  }, [cardDetails, hasBankingUser, pageLoading]);

  const isEnabledUserOverview = useIsEnabledUserOverview();

  const {breadcrumbs, documentTitle} = useMemo(() => {
    let title = '';
    const cardHoldMessage = t('cards:errors.hold');
    const displayedCardName = cardNumber || cardDetails?.mainData?.name;
    if (!pageLoading.exists && !pageLoading.loading) {
      title = t('elementNotFound', {name: t('cards:card')});
    } else if (displayedCardName) {
      title = `${displayedCardName} ${t('detail')}`;
    } else if (cardHold) {
      title = cardHoldMessage;
    }
    const defaultBreadcrumbs = [
      {title: t('navigation.cards'), href: routes.cardsList},
      {title}
    ];
    return {
      breadcrumbs: systemHelpers.getUserBreadcrumbs({defaultBreadcrumbs, isAdmin, isEnabledUserOverview, t}),
      documentTitle: cardHoldMessage !== title ? `${t('pageTitles.cardDetails')} ${title}` : cardHoldMessage,
    }
  }, [t, cardDetails?.mainData, cardHold, cardNumber, pageLoading, isAdmin, isEnabledUserOverview]);

  const authModalProps = useMemo(() => scaHelpers.getAuthModalProps({action: activeAuthAction, t}), [activeAuthAction, t]);

  const finishLoading = (exists = true) => setPageLoading({exists, loading: false});

  useEffect(() => {
    if (!isValidValue(cardId) || !isValidValue(userId)) return;

    setPageLoading({...pageLoading, loading: true});
    setTransactions({...transactions, isLoaded: false});

    // get information about card details from backend
    getMainCardDetails(
      cardId,
      (mainData) => {
        const subscriptionId = mainData?.subscription_id;
        // get information about card subscription
        if (subscriptionId) {
          getSubscription(
            subscriptionId,
            (subscription) => setSubscription(subscription)
          );
        }
        // get information about card details from banking
        getBankingCardDetails(
          userId,
          cardId,
          (bankingData) => {
            setCardDetails({
              ...cardDetails,
              bankingData,
              mainData
            });
            getTransactions(bankingData);
            finishLoading();
          },
          () => {
            setCardDetails({
              ...cardDetails,
              mainData
            });
            finishLoading();
            setTransactions({...transactions, isLoaded: true});
          }
        );

        // check whether the cardholder is registered with the bank
        if (isAdmin) {
          getUserDetails(
            userId,
            () => setHasBankingUser(true),
            () => setHasBankingUser(false)
          );
        }
      },
      () => finishLoading(false)
    );
  }, [cardId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const transactionsWithExpense = transactionsHelpers.getTransactionsWithExpense({
      expenses,
      employees,
      transactions: transactions.transactions
    });
    setTransactionsWithExpense(transactionsWithExpense);
  }, [transactions, expenses, employees]);

  const getTransactions = (card) => {
    loadTransactionsList({
      card: card || cardDetails?.bankingData,
      operationName: TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION,
      query: {},
      successCallback: (response) => getTransactionsSuccessCallback(response, true),
      errorCallback: () => setTransactions({...transactions, isLoaded: true, transactions: []})
    });
  }

  const handleCloseAuthModal = () => {
    const operationActions = {
      [TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION]: finishLoading,
      [TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION]: finishLoading,
    }
    setAuthWindowProps({...authWindowProps, open: false});
    if (operationActions.hasOwnProperty(activeAuthAction)) operationActions[activeAuthAction]();
  }

  const handleOnSuccessAuth = () => {
    const operationActions = {
      [TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION]: handleLoadMore,
      [TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION]: getTransactions,
    }
    setAuthWindowProps({...authWindowProps, open: false});
    if (operationActions.hasOwnProperty(activeAuthAction)) operationActions[activeAuthAction]();
  }

  const getTransactionsSuccessCallback = (response, initial = false) => {
    const responseTransactions = response?.results || [];
    const cursor = response?.pagination?.next_cursor || undefined;
    const expensesIds = responseTransactions.map(t => t.expense_id).filter(value => value);

    setTransactions({
      transactions: initial ? responseTransactions : [...transactions.transactions, ...responseTransactions],
      isLoaded: true
    });

    if (Boolean(expensesIds.length)) {
      getBatchExpenses({
        data: {expenses_ids: expensesIds},
        successCallback: (responseExpenses) => setExpenses(initial ? responseExpenses : [...expenses, ...responseExpenses])
      });
    }

    setLoadMoreProps({
      ...loadMoreProps,
      cursor,
      loading: false,
      isEnabled: Boolean(cursor),
    });
  }

  const loadTransactionsList = ({
    query,
    successCallback,
    errorCallback,
    operationName
  }) => {
    const requestQuery = {
      sort_order: 'desc',
      limit: transactionsRequestLimit,
      ...query
    }

    if (operationName && operationName !== activeAuthAction) setActiveAuthAction(operationName);

    getTransactionsList({
      cardId,
      userId,
      headers: scaHelpers.getAuthHeaders(operationName || activeAuthAction),
      query: requestQuery,
      successCallback,
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
          errorCallback
        });
      }
    });
  }

  const handleLoadMore = () => {
    if (loadMoreProps.isEnabled) {
      const cursor = loadMoreProps?.cursor;
      setLoadMoreProps({...loadMoreProps, loading: true});

      loadTransactionsList({
        operationName: TRANSACTIONS_OLDER_90_DAYS_LOADING_ACTION,
        query: { cursor },
        successCallback: getTransactionsSuccessCallback,
        errorCallback: () => setLoadMoreProps({...loadMoreProps, loading: false})
      });
    }
  }

  const handleCloseTransactionDetails = () => {
    setIsOpenDetails({card: true, transaction: false});
    selectedTransactionRowIndex && setSelectedTransactionRowIndex(undefined);
  }

  const handleOnRowClick = (transactionId, index) => {
    let transaction = transactionsWithExpense.find(d => d.id === transactionId);
    if (transaction) {
      setSelectedTransaction(transaction);
      setIsOpenDetails({card: false, transaction: true});
      logEvent(firebaseEvents.CARDS_OPEN_TRANSACTION_DETAILS);
    }
    selectedTransactionRowIndex !== index && setSelectedTransactionRowIndex(index);
  }

  const onExpenseUpdate = (updatedExpense, actionType) => {
    if (updatedExpense) {
      let updatedExpenses = transactionsHelpers.getUpdatedBatchExpensesList(expenses, updatedExpense, actionType);
      setExpenses(updatedExpenses);
      if (selectedTransaction) {
        setSelectedTransaction({
          ...selectedTransaction,
          expense: {
            ...selectedTransaction.expense,
            ...updatedExpense
          }
        })
      }
    }
    systemHelpers.logEvent(firebaseEvents.TRANSACTIONS_EDIT_TRANSACTION_DETAILS);
  }

  const {exists, loading} = pageLoading;

  return (
    <PageContainer>
      <PageDocumentDetails title={documentTitle} />
      <BalancePageHeader breadcrumbs={breadcrumbs} />
      <SpinSmall spinning={loading}>
        <StyledCardPageContainer>

          {exists ? (
            <StyledCardPageContainerSpace
              align='start'
              size='large'
            >
              <TransactionsPanel
                edit={isEnabledEdit}
                expenses={expenses}
                loading={!transactions.isLoaded}
                loadMoreProps={{
                  ...loadMoreProps,
                  onClick: handleLoadMore
                }}
                transactions={transactionsWithExpense}
                onRowClick={handleOnRowClick}
                selectedRowIndex={selectedTransactionRowIndex}
              />
              <StyledCardPageTransactionContainer>
                <Transition
                  nodeRef={cardDetailsNodeRef}
                  in={isOpenDetails.card}
                  timeout={duration}
                >
                  {state => (
                    <CardDetailsSection
                      cardHold={cardHold}
                      cardHoldMessage={!hasBankingUser && t('cards:errors.theUserNeedToFinishLightKYC')}
                      cardDetails={cardDetails}
                      edit={isEnabledEdit}
                      onUpdateCardDetails={setCardDetails}
                      pageLoading={pageLoading}
                      style={{
                        ...detailsTransitionStyles.default,
                        ...detailsTransitionStyles[state]
                      }}
                      subscription={subscription}
                    />
                  )}
                </Transition>
                <Transition
                  nodeRef={transactionDetailsNodeRef}
                  in={isOpenDetails.transaction}
                  timeout={duration}
                >
                  {state => (
                    <TransactionExpenseDetailsPanel
                      className='expense-details-panel'
                      onClose={handleCloseTransactionDetails}
                      onExpenseUpdate={onExpenseUpdate}
                      style={{
                        ...detailsTransitionStyles.default,
                        ...detailsTransitionStyles[state]
                      }}
                      subscription={subscription}
                      transaction={selectedTransaction}
                    />
                  )}
                </Transition>
              </StyledCardPageTransactionContainer>
            </StyledCardPageContainerSpace>
          ) : !loading && <NotFoundPage />}

        </StyledCardPageContainer>
      </SpinSmall>

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={authModalProps}
        handleCancel={handleCloseAuthModal}
        onSuccess={handleOnSuccessAuth}
        operationName={activeAuthAction}
      />
    </PageContainer>
  );
}

const mapStateToProps = state => {
  const {employees} = state.company;
  const {isAdmin, employee} = state.user;

  return {
    employees,
    employeeId: employee?.id,
    isAdmin
  }
}

const mapDispatchToProps = {
  getBatchExpenses: expenseActions.getBatchExpenses,
  getBankingCardDetails: bankingActions.getUserCardDetails,
  getMainCardDetails: cardActions.getCard,
  getSubscription: subscriptionActions.getSubscription,
  getTransactionsList: transactionActions.getUserCardTransactionsList,
  getUserDetails: bankingActions.getUserDetails,
}

export default connect(mapStateToProps, mapDispatchToProps)(CardPage);
